// components/CreateTaskModal.jsx
import React, { useState, useEffect } from "react";
import { X, Bell } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CreateTaskModal = ({ opportunity, account, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();

  // Resolve context — opportunity takes precedence over account
  const contextLabel = opportunity ? opportunity.name : account?.name || "";
  const initialData = opportunity
    ? {
        opportunityId: opportunity.id,
        accountId: opportunity.accountId || null,
      }
    : { accountId: account?.id || null };

  const [formData, setFormData] = useState({
    ...initialData,
    title: "",
    description: "",
    status: "todo",
    priority: "medium",
    dueDate: "",
    assignedTo: null,
    reminderEnabled: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await apiService.getCompanyUsers();
      setUsers(response.data || []);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const statusOptions = [
    "todo",
    "in_progress",
    "review",
    "completed",
    "blocked",
  ];
  const priorityOptions = ["low", "medium", "high", "urgent"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await apiService.createTask(formData);
      onSuccess();
    } catch (err) {
      console.error("Error creating task:", err);
      setError(err.response?.data?.message || "Failed to create task");
    } finally {
      setLoading(false);
    }
  };

  const reminderDisabled = !formData.dueDate;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto ${
          isDarkMode
            ? "bg-[#13161B] border border-gray-700"
            : "bg-white border border-gray-300"
        }`}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2
              className={`text-xl font-bold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Create New Task
            </h2>
            <button
              onClick={onClose}
              className={`transition-colors ${
                isDarkMode
                  ? "text-gray-400 hover:text-white"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {error && (
            <div
              className={`mb-4 p-3 rounded-lg text-sm ${
                isDarkMode
                  ? "bg-red-500/10 border border-red-500/50 text-red-400"
                  : "bg-red-50 border border-red-200 text-red-600"
              }`}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
                placeholder="Enter task title"
              />
            </div>

            {/* Context field — shows Opportunity or Account name, read-only */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                {opportunity ? "Opportunity" : "Account"}
              </label>
              <input
                type="text"
                value={contextLabel}
                disabled
                className={`w-full px-4 py-2 border rounded-lg cursor-not-allowed ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-700 text-gray-400"
                    : "bg-gray-100 border-gray-300 text-gray-500"
                }`}
              />
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Description
              </label>
              <textarea
                rows="4"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className={`w-full px-4 py-2 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
                placeholder="Enter task description"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status.replace("_", " ").toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                  }
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  {priorityOptions.map((priority) => (
                    <option key={priority} value={priority}>
                      {priority.charAt(0).toUpperCase() + priority.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Due date + reminder row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Due Date
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => {
                    const newDueDate = e.target.value || "";
                    setFormData((prev) => ({
                      ...prev,
                      dueDate: newDueDate,
                      // clear reminder if due date is removed
                      reminderEnabled: newDueDate
                        ? prev.reminderEnabled
                        : false,
                    }));
                  }}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              {/* Email reminder toggle */}
              <div className="flex flex-col justify-end">
                <button
                  type="button"
                  disabled={reminderDisabled}
                  onClick={() => {
                    if (!reminderDisabled) {
                      setFormData((prev) => ({
                        ...prev,
                        reminderEnabled: !prev.reminderEnabled,
                      }));
                    }
                  }}
                  className={`flex items-center gap-3 px-4 py-2 rounded-lg border transition-colors ${
                    reminderDisabled
                      ? isDarkMode
                        ? "opacity-40 cursor-not-allowed border-gray-700 bg-transparent"
                        : "opacity-40 cursor-not-allowed border-gray-300 bg-transparent"
                      : formData.reminderEnabled
                        ? "border-blue-500 bg-blue-500/10 cursor-pointer"
                        : isDarkMode
                          ? "border-gray-700 bg-transparent hover:border-gray-500 cursor-pointer"
                          : "border-gray-300 bg-transparent hover:border-gray-400 cursor-pointer"
                  }`}
                >
                  <Bell
                    className={`w-4 h-4 flex-shrink-0 ${
                      formData.reminderEnabled
                        ? "text-blue-400"
                        : isDarkMode
                          ? "text-gray-400"
                          : "text-gray-500"
                    }`}
                  />
                  <div className="text-left">
                    <p
                      className={`text-sm font-medium leading-tight ${
                        formData.reminderEnabled
                          ? "text-blue-400"
                          : isDarkMode
                            ? "text-gray-300"
                            : "text-gray-700"
                      }`}
                    >
                      Email Reminder
                    </p>
                    <p
                      className={`text-xs leading-tight mt-0.5 ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      {reminderDisabled
                        ? "Set a due date first"
                        : formData.reminderEnabled
                          ? "Day before & on the day"
                          : "Off"}
                    </p>
                  </div>

                  {/* Toggle pill */}
                  <div className="ml-auto flex-shrink-0">
                    <div
                      className={`w-9 h-5 rounded-full transition-colors relative ${
                        formData.reminderEnabled
                          ? "bg-blue-500"
                          : isDarkMode
                            ? "bg-gray-600"
                            : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                          formData.reminderEnabled
                            ? "translate-x-4"
                            : "translate-x-0.5"
                        }`}
                      />
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "bg-[#0C0E12] border border-gray-700 text-gray-300 hover:bg-gray-700"
                    : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 rounded-lg text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: isDarkMode
                    ? "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)"
                    : "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                }}
              >
                {loading ? "Creating..." : "Create Task"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateTaskModal;
